"use client";

import React, { useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { Button } from "@concierge/widget-ui/components/ui/button";
import { cn } from "@concierge/widget-ui/lib/utils";

export interface WidgetPopupProps {
  className?: string;
  children?: React.ReactNode;
}

export function WidgetPopup({ className, children }: WidgetPopupProps) {
  const [isOpen, setIsOpen] = useState(false);

  const togglePopup = () => {
    console.log("Toggle popup clicked, current state:", isOpen);
    setIsOpen(!isOpen);
  };

  const handleOverlayClick = () => {
    console.log("Overlay clicked");
    togglePopup();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    console.log("Content clicked");
    e.stopPropagation();
  };

  return (
    <>
      {/* Overlay - only visible when open */}
      {isOpen && (
        <div
          className={cn(
            "fixed inset-0 bg-black/20 backdrop-blur-sm z-40",
            "transition-opacity duration-200 ease-out",
            "opacity-100",
          )}
          onClick={handleOverlayClick}
        />
      )}

      {/* Widget Container */}
      <div className={cn("fixed bottom-6 right-6 z-50", className)}>
        {/* Content Panel */}
        <div
          className={cn(
            "absolute bg-card border border-border rounded-xl shadow-2xl overflow-hidden",
            "transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] origin-bottom-right",
            "pointer-events-auto",
            isOpen
              ? "opacity-100 w-[calc(100vw-48px)] sm:w-110 h-[calc(100vh-112px)] sm:h-[650px] bottom-16 right-0"
              : "opacity-0 w-16 h-16 bottom-6 right-6 pointer-events-none",
          )}
          onClick={handleContentClick}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-background/80 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="size-8 bg-primary rounded-full flex items-center justify-center">
                <MessageCircle className="size-4 text-primary-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-foreground">
                  Chat Assistant
                </h3>
                <p className="text-xs text-muted-foreground">
                  How can I help you today?
                </p>
              </div>
            </div>
            {/*<Button
              variant="ghost"
              size="icon-sm"
              onClick={togglePopup}
              className="shrink-0 hover:bg-muted transition-colors"
            >
              <X className="size-4" />
            </Button>*/}
          </div>

          {/* Content Area */}
          <div className="flex-1 h-[calc(100vh-185px)] sm:h-[calc(650px-73px)] overflow-y-auto">
            {children || (
              <div className="flex items-center justify-center h-full text-muted-foreground p-4">
                <div className="text-center space-y-2">
                  <MessageCircle className="size-12 mx-auto opacity-50" />
                  <p className="text-sm">Start a conversation</p>
                  <p className="text-xs opacity-75">
                    Click here to begin chatting
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Floating Action Button */}
        <Button
          onClick={togglePopup}
          size="icon-lg"
          className={cn(
            "size-14 z-[9999] rounded-full shadow-lg hover:shadow-xl transition-all duration-200",
            "bg-primary hover:bg-primary/90 text-primary-foreground",
            "border-2 border-background/20 relative overflow-hidden",
            "transform hover:scale-105 active:scale-95",
          )}
        >
          <div
            className={cn(
              "transition-transform duration-300 ease-in-out",
              isOpen && "rotate-180",
            )}
          >
            {isOpen ? (
              <X className="size-6" />
            ) : (
              <MessageCircle className="size-6" />
            )}
          </div>
        </Button>

        {/* Pulse Animation Ring (when closed) */}
        {!isOpen && (
          <>
            <div className="absolute inset-0 rounded-full bg-primary opacity-20 animate-ping" />
            <div
              className="absolute inset-0 rounded-full bg-primary opacity-10 animate-ping"
              style={{ animationDelay: "0.5s", animationDuration: "2s" }}
            />
          </>
        )}
      </div>
    </>
  );
}

export default WidgetPopup;
